import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
} from '@mui/material';

export interface ConfirmDialogProps {
    /** Whether dialog is open */
    open: boolean;
    /** Dialog title */
    title: string;
    /** Dialog message/description */
    message: string;
    /** Callback when confirmed */
    onConfirm: () => void;
    /** Callback when cancelled or closed */
    onCancel: () => void;
    /** Whether action is in progress (shows spinner, disables buttons) */
    loading?: boolean;
    /** Button variant for styling */
    variant?: 'delete' | 'confirm' | 'warning';
    /** Custom confirm button text (default based on variant) */
    confirmText?: string;
    /** Custom cancel button text (default: "Cancel") */
    cancelText?: string;
}

/**
 * Reusable confirmation dialog for delete/confirm actions.
 *
 * Variants:
 * - delete: Red confirm button, "Delete" text
 * - confirm: Primary color confirm button, "Confirm" text
 * - warning: Warning color confirm button, "Proceed" text
 */
export function ConfirmDialog({
    open,
    title,
    message,
    onConfirm,
    onCancel,
    loading = false,
    variant = 'confirm',
    confirmText,
    cancelText = 'Cancel',
}: ConfirmDialogProps) {
    const getConfirmButtonProps = () => {
        switch (variant) {
            case 'delete':
                return {
                    color: 'error' as const,
                    text: confirmText || 'Delete',
                };
            case 'warning':
                return {
                    color: 'warning' as const,
                    text: confirmText || 'Proceed',
                };
            case 'confirm':
            default:
                return {
                    color: 'primary' as const,
                    text: confirmText || 'Confirm',
                };
        }
    };

    const buttonProps = getConfirmButtonProps();

    return (
        <Dialog open={open} onClose={onCancel}>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                <DialogContentText>{message}</DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel} disabled={loading}>
                    {cancelText}
                </Button>
                <Button
                    onClick={onConfirm}
                    color={buttonProps.color}
                    variant="contained"
                    disabled={loading}
                >
                    {loading ? <CircularProgress size={20} /> : buttonProps.text}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default ConfirmDialog;
